export const SERVICES = [
  {
    slug: "consultancy",
    title: "Consultancy for System Development",
    blurb: "Architecture, discovery, and delivery roadmaps for software that fits how you work.",
    tag: "Strategy",
  },
  {
    slug: "custom-development",
    title: "Custom System Development",
    blurb: "Purpose-built web and internal systems — from workflow tools to customer portals.",
    tag: "Build",
  },
  {
    slug: "website-development",
    title: "Website Development",
    blurb:
      "Marketing sites, landing pages, and content-driven web presence — designed, built, and kept current.",
    tag: "Web",
  },
  {
    slug: "erp",
    title: "Enterprise Resource Planning (ERP)",
    blurb:
      "JD ERP with 8 subscription modules — POS, accounting, HR & payroll, procurement, inventory, sales, marketing, and production.",
    tag: "Operations",
  },
  {
    slug: "mail-hosting",
    title: "Mail Hosting Services",
    blurb:
      "Business email on your clients’ domains — mailboxes, spam protection, and reliable delivery powered by the same stack as JD Mail.",
    tag: "Hosting",
  },
  {
    slug: "repair",
    title: "Computer and Hardware Repair",
    blurb: "Diagnostics and repair for workstations, servers, and peripherals that keep teams online.",
    tag: "Bench",
  },
  {
    slug: "parts",
    title: "Sells computer parts",
    blurb: "Sourced components and upgrades for reliable builds — desktops, laptops, and infra.",
    tag: "Supply",
  },
] as const;

export const SITE = {
  name: "JD INTEGRATIONS",
  legalName: "JD Integrations Enterprise Solutions Corp.",
  tagline: "AUTOMATE · INTEGRATE · DOMINATE",
  blurb: "Systems, service, and the JD Apps suite — built as one family.",
  parent: {
    name: "JD LEGACY GROUP OF COMPANIES CORP.",
    short: "JD Legacy Group",
    logo: "/jd-legacy-logo.webp",
    url: "https://jdlegacy.net",
    relation: "A subsidiary of JD LEGACY GROUP OF COMPANIES CORP.",
  },
  location: {
    label: "Principal office",
    addressLines: [
      "Unit 1 Dimla St., Purok 6 Anyatam, San Ildefonso,",
      "Bulacan, Region III (Central Luzon), 3010",
    ],
    streetAddress: "Unit 1 Dimla St., Purok 6 Anyatam, San Ildefonso",
    addressLocality: "San Ildefonso",
    addressRegion: "Bulacan",
    postalCode: "3010",
    addressCountry: "PH",
    coordsLabel: "15°06'05.1\"N 120°57'00.5\"E",
    lat: 15.101421,
    lng: 120.950134,
    mapsUrl: "https://maps.app.goo.gl/WGQqKWgnB1dfcukg8",
  },
};

/** Sister subsidiaries under JD Legacy Group (alongside JD Integrations). */
export const GROUP_COMPANIES = [
  {
    slug: "integrations",
    name: "JD Integrations Enterprise Solutions Corp.",
    focus: "Technology & systems",
    blurb: "Software, ERP, hardware service, and the JD Apps suite.",
    logo: "/logo.webp",
    url: "https://jdintegrations.net",
    current: true,
  },
  {
    slug: "properties",
    name: "JD Properties",
    focus: "Real estate",
    blurb: "Property and real estate under the JD Legacy Group.",
    logo: "/jd-properties-logo.webp",
    current: false,
  },
  {
    slug: "delight",
    name: "JD Delight Creations",
    focus: "Food · cakes & pastries",
    blurb: "Cakes, pastries, and food creations under the JD Legacy Group.",
    logo: "/jd-delight-logo.webp",
    current: false,
  },
] as const;

export const SOCIALS = [
  {
    name: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61573253949524",
  },
  {
    name: "TikTok",
    href: "https://www.tiktok.com/@jdintegrations",
  },
] as const;

export const HERO_SLIDES = [
  {
    id: "systems",
    tone: "tone-systems",
    headline: "Systems that fit how your business actually runs.",
    support: "Consultancy, build, ERP, repair, and parts — one partner for the stack.",
    cta: { href: "/services", label: "View services" },
  },
  {
    id: "build",
    tone: "tone-build",
    headline: "Custom software shaped around your workflows.",
    support: "From discovery to go-live — portals, internals, and tools your team will use.",
    cta: { href: "/contact?intent=quote&service=Custom%20System%20Development", label: "Start a build" },
  },
  {
    id: "erp",
    tone: "tone-erp",
    headline: "JD ERP — eight modules, activate what you need.",
    support:
      "POS, accounting, HR & payroll, procurement, inventory, sales, marketing, and production — one secure subscription framework.",
    cta: { href: "/services#jd-erp", label: "See ERP modules" },
  },
  {
    id: "systems-built",
    tone: "tone-build",
    headline: "SAMS, HAMS, and CAMS — systems already in the field.",
    support:
      "School, hospital, and clinic administration platforms we built for real institutions.",
    cta: { href: "/services#built-systems", label: "View systems" },
  },
  {
    id: "bench",
    tone: "tone-bench",
    headline: "Hardware that stays online when it matters.",
    support: "Diagnostics, repair, and parts for workstations, servers, and everyday gear.",
    cta: { href: "/contact?intent=quote&service=Computer%20and%20Hardware%20Repair", label: "Book repair" },
  },
  {
    id: "apps",
    tone: "tone-apps",
    headline: "JD Apps — mail, drive, calendar, task, CRM, CMS, analytics, and ERP as one suite.",
    support: "Warm HUD tooling with JD SSO, built for the same team that ships client work.",
    cta: { href: "/about", label: "About JD" },
  },
] as const;

export const CAPABILITY_RIBBON = [
  "SAMS",
  "HAMS",
  "CAMS",
  "Flexible payment terms",
  "Affordable pricing",
  "Referral partners welcome",
  "Referral rewards",
  "JD ERP · 8 modules",
  "System consultancy",
  "Custom development",
  "Hardware repair",
  "Computer parts",
  "JD Mail",
  "JD Drive",
  "JD Calendar",
  "JD Task",
  "JD CRM",
  "JD CMS",
  "JD Analytics",
  "JD Account",
] as const;

/** Client / domain systems already built by JD Integrations. */
export const BUILT_SYSTEMS = [
  {
    slug: "sams",
    acronym: "SAMS",
    title: "School Administration and Management System",
    blurb:
      "Admissions, enrollment, academics, attendance, billing, and records — one system for the school day.",
    audience: "Schools & academies",
  },
  {
    slug: "hams",
    acronym: "HAMS",
    title: "Hospital Administration and Management System",
    blurb:
      "Patient flow, wards, billing, pharmacy, and operations for hospital-scale care coordination.",
    audience: "Hospitals",
  },
  {
    slug: "cams",
    acronym: "CAMS",
    title: "Clinic Administration and Management System",
    blurb:
      "Appointments, patient records, billing, and day-to-day clinic operations in a lighter footprint.",
    audience: "Clinics",
  },
] as const;

export const ERP_PRODUCT = {
  slug: "jd-erp",
  acronym: "ERP",
  title: "JD ERP",
  fullTitle: "Enterprise Resource Planning",
  tagline: "Integrated ERP · 8 subscription modules",
  blurb:
    "A highly secure, scalable ERP platform for multi-organization workflows. Deploy only what you need today — activate new modules as you grow, with affordable pricing and flexible payment terms.",
  modules: [
    {
      n: "01",
      name: "Point of Sale (POS)",
      body: "Omnichannel retail transactions with live inventory and accounting sync, digital receipts, and real-time stock and revenue logging.",
    },
    {
      n: "02",
      name: "Financial & Accounting",
      body: "General ledger, journals, supplier bills, customer invoices, payment allocation, and live financial dashboards.",
    },
    {
      n: "03",
      name: "HR & Payroll",
      body: "Employee master data, departments, attendance-tied payroll, compliance reporting, and automated labor cost journals.",
    },
    {
      n: "04",
      name: "Procurement & Supply Chain",
      body: "Purchase requests, multi-tier approvals, goods receipts, and PO-to-bill matching that drafts vendor accounts and journals.",
    },
    {
      n: "05",
      name: "Inventory Management",
      body: "Real-time stock across locations, movements linked to sales and receipts, low-stock alerts, and no double-entry.",
    },
    {
      n: "06",
      name: "Sales & Order Fulfillment",
      body: "Order-to-cash from generation to fulfillment, with automated invoicing and journals when Accounting is active.",
    },
    {
      n: "07",
      name: "Marketing Pipeline & Campaigns",
      body: "Lead generation, pipeline progression, and campaign logging linked to subsequent customer deals.",
    },
    {
      n: "08",
      name: "Production Management",
      body: "Manufacturing schedules, operational oversight, and material resource tracking for complex structures.",
    },
  ],
} as const;

export const PILLARS = [
  {
    title: "Software that matches the floor",
    body: "We design around real workflows — not generic templates — so tools stick after launch.",
  },
  {
    title: "Hardware when uptime matters",
    body: "Bench diagnostics, repair, and parts keep desks and servers from becoming blockers.",
  },
  {
    title: "Affordable, flexible terms",
    body: "Clear pricing you can plan around — with flexible payment terms so growth is not blocked by cash flow.",
  },
] as const;

export const VALUE_TERMS = {
  label: "Pricing · terms · partners",
  title: "Affordable rates. Flexible terms. Referral rewards.",
  body: "We keep pricing practical for growing teams, offer flexible payment arrangements, and welcome referral partners — with rewards when you introduce work that lands.",
  points: [
    {
      title: "Affordable pricing",
      body: "Scoped quotes that fit real budgets — deploy what you need, expand modules as you grow.",
    },
    {
      title: "Flexible payment terms",
      body: "Payment schedules you can work with — staged deliveries and terms aligned to your cash flow.",
    },
    {
      title: "Referral partners",
      body: "We accept referral partners and offer referral rewards when introductions turn into projects.",
    },
  ],
} as const;

/** Public pricing — modular list rates (ERP-style psychology). Quotes via Contact. */
export const PRICING_META = {
  currency: "₱",
  bundleDiscountPercent: 10,
  trialDays: 7,
  capabilityPreview: 4,
  antiBundleLine:
    "Subscribe per module — without forcing a monolithic ERP bundle.",
  listRatesNote:
    "List rates shown here anchor expectations. Actual billing is set by the platform team — contact us for org-specific quotes, bundle codes, and payment terms.",
  trialLine:
    "Start with a 7-day trial of the integrated stack — experience the full mesh, then keep only the modules you need.",
} as const;

export const PRICING_SOCIAL_PROOF = [
  { value: "120+", label: "Organizations on JD systems" },
  { value: "20k+", label: "CMS postings managed" },
  { value: "38%", label: "Avg. process time reduced" },
] as const;

export type PricingModuleStatus = "available" | "coming-soon";
export type PricingModuleRole = "floor" | "mid" | "ceiling";

export const PRICING_MODULES = [
  {
    id: "suite-calendar",
    name: "JD Calendar",
    category: "suite" as const,
    role: "floor" as const,
    monthlyList: 1800,
    status: "available" as const,
    tagline: "Shared schedules and availability across the org.",
    capabilities: [
      "Team calendars with shared visibility rules",
      "Invites wired to JD Mail threads",
      "Availability hints for scheduling",
      "Same SSO and chrome as the suite",
      "Mobile-friendly month and agenda views",
    ],
    service: "JD Calendar module",
  },
  {
    id: "suite-task",
    name: "JD Task",
    category: "suite" as const,
    role: "floor" as const,
    monthlyList: 1800,
    status: "available" as const,
    tagline: "Work queues beside mail and calendar — not another silo.",
    capabilities: [
      "Assignees synced from JD Account",
      "Queues linked to mail and calendar context",
      "Status lanes your team actually uses",
      "Notifications through the suite mesh",
      "Lightweight boards — no heavyweight PM bloat",
    ],
    service: "JD Task module",
  },
  {
    id: "suite-analytics",
    name: "JD Analytics",
    category: "suite" as const,
    role: "mid" as const,
    monthlyList: 2200,
    status: "available" as const,
    tagline: "First-party traffic insight for your public properties.",
    capabilities: [
      "Privacy-forward analytics under your domain",
      "Site and campaign traffic dashboards",
      "First-party script — no third-party ad pixels",
      "Works with CMS-published pages",
      "Export-friendly summaries for stakeholders",
    ],
    service: "JD Analytics module",
  },
  {
    id: "suite-crm",
    name: "JD CRM",
    category: "suite" as const,
    role: "mid" as const,
    monthlyList: 2800,
    status: "available" as const,
    tagline: "Leads, deals, and pipeline beside your public site.",
    capabilities: [
      "Website contact forms ingest to CRM",
      "Deal stages and weekly pipeline rhythm",
      "Activity history tied to org users",
      "Shared chrome with Mail and Task",
      "Referral-friendly partner workflows",
    ],
    service: "JD CRM module",
  },
  {
    id: "suite-cms",
    name: "JD CMS",
    category: "suite" as const,
    role: "mid" as const,
    monthlyList: 2800,
    status: "available" as const,
    tagline: "Posts, pages, and media that power your domain.",
    capabilities: [
      "Blog and page publishing for your site",
      "Media library with cover optimization",
      "Domain-scoped content for JDI / JDL properties",
      "Authors provisioned via JD Account",
      "Feeds CRM and Analytics when both are active",
    ],
    service: "JD CMS module",
  },
] as const;

/** Example multi-module bundle for discount framing on /pricing. */
export const PRICING_BUNDLE_EXAMPLE_IDS = [
  "suite-calendar",
  "suite-task",
  "suite-crm",
  "suite-cms",
] as const;

export function formatPeso(amount: number): string {
  return `${PRICING_META.currency}${amount.toLocaleString("en-PH")}`;
}

export function pricingModulesAvailable() {
  return PRICING_MODULES.filter((m) => m.status === "available");
}

export function pricingRange() {
  const mods = pricingModulesAvailable();
  if (mods.length === 0) return { min: 0, max: 0 };
  const prices = mods.map((m) => m.monthlyList);
  return { min: Math.min(...prices), max: Math.max(...prices) };
}

export function fullStackListValue() {
  return pricingModulesAvailable().reduce((sum, m) => sum + m.monthlyList, 0);
}

/** Checkout-eligible module IDs (JD Apps only — rejects erp-* and coming-soon). */
export function isCheckoutModuleId(id: string): boolean {
  const mod = PRICING_MODULES.find((m) => m.id === id);
  return !!mod && mod.status === "available" && mod.category === "suite";
}

export function bundleQuote(moduleIds: readonly string[]) {
  const mods = PRICING_MODULES.filter(
    (m) => moduleIds.includes(m.id) && m.status === "available",
  );
  const subtotal = mods.reduce((sum, m) => sum + m.monthlyList, 0);
  const discount = Math.round(
    (subtotal * PRICING_META.bundleDiscountPercent) / 100,
  );
  return { subtotal, discount, total: subtotal - discount, count: mods.length };
}

/** @deprecated Tier cards replaced by modular catalog — kept for reference. */
export const PRICING_PLANS = [
  {
    slug: "starter",
    name: "Starter",
    tag: "Small teams",
    price: "Per seat · monthly",
    blurb: "One domain, JD Account SSO, and the core apps to stop juggling logins.",
    featured: false,
    includes: [
      "JD Account — tenants, mailboxes, app grants",
      "JD Mail, Calendar, and Task",
      "Shared chrome and one sign-in",
      "Email support during business hours",
    ],
    cta: "Request Starter quote",
    service: "JD Apps Starter plan",
  },
  {
    slug: "team",
    name: "Team",
    tag: "Growing orgs",
    price: "Per seat · monthly",
    blurb: "Pipeline and publishing in the suite — website leads and the public blog under one SSO.",
    featured: true,
    includes: [
      "Everything in Starter",
      "JD CRM — leads, deals, weekly pipeline",
      "JD CMS — posts, pages, media for your domain",
      "JD Analytics — first-party site insight",
      "Website contact forms into CRM",
    ],
    cta: "Request Team quote",
    service: "JD Apps Team plan",
  },
  {
    slug: "org",
    name: "Organization",
    tag: "Ops-ready",
    price: "Seats + modules",
    blurb: "Full suite mesh plus JD ERP modules you activate as the floor needs them.",
    featured: false,
    includes: [
      "Everything in Team",
      "JD ERP — activate modules as you grow",
      "Priority support and onboarding help",
      "Referral partner terms available",
      "Flexible payment schedules on setup",
    ],
    cta: "Request Org quote",
    service: "JD Apps Organization plan",
  },
] as const;

/** JD Mail — per mailbox on the org domain (monthly vs annual). */
export const MAILBOX_PRICING = {
  monthlyPerMailbox: 100,
  annualPerMailbox: 1000,
  currency: "₱",
  name: "JD Mail mailbox",
  tag: "Your domain",
  blurb: "Professional inbox on your org domain — same SSO and chrome as the JD Apps suite.",
  includes: [
    "Webmail, IMAP, and SMTP on your org domain",
    "IMAP & SMTP for desktop and mobile clients",
    "JD Account mailbox provisioning",
    "Add or remove seats as your team changes",
  ],
  ctaMonthly: "Start monthly",
  ctaAnnual: "Choose annual",
  service: "JD Mail mailbox",
} as const;

export function mailboxPricingMath() {
  const { monthlyPerMailbox, annualPerMailbox } = MAILBOX_PRICING;
  const monthlyYearTotal = monthlyPerMailbox * 12;
  const savePerMailbox = monthlyYearTotal - annualPerMailbox;
  const savePercent = Math.round((savePerMailbox / monthlyYearTotal) * 100);
  return { monthlyYearTotal, savePerMailbox, savePercent };
}

export const PRICING_ADDONS = [
  {
    title: "Implementation & migration",
    body: "Discovery, data cleanup, training, and go-live so the suite sticks after day one.",
  },
  {
    title: "Custom software",
    body: "Purpose-built modules beside JD Apps when off-the-shelf would bend your process.",
  },
  {
    title: "Website & hosting",
    body: "Marketing site, CMS publishing, and mail hosting on your domain.",
  },
  {
    title: "Hardware care",
    body: "Repair, parts, and preventive care so machines do not block the work.",
  },
] as const;

export const FOCUS_BANDS = [
  {
    id: "software",
    label: "Built systems",
    title: "SAMS, HAMS, CAMS — and JD ERP with eight modules.",
    body: "Domain systems already delivered for schools, hospitals, and clinics, plus a modular ERP you can roll out by need.",
    points: [
      "School Administration and Management System (SAMS)",
      "Hospital Administration and Management System (HAMS)",
      "Clinic Administration and Management System (CAMS)",
      "JD ERP — POS, Accounting, HR & Payroll, Procurement, Inventory, Sales, Marketing, Production",
    ],
    href: "/services#built-systems",
    cta: "Explore products",
  },
  {
    id: "hardware",
    label: "Bench & supply",
    title: "Keep the machines under the work running.",
    body: "When a workstation, server, or peripheral fails, we diagnose, repair, or source the part — without the runaround.",
    points: [
      "Workstation and server repair",
      "Component upgrades and sourcing",
      "Practical advice on what to replace vs rebuild",
    ],
    href: "/contact?intent=quote&service=Computer%20and%20Hardware%20Repair",
    cta: "Book the bench",
  },
] as const;

export const AUDIENCES = [
  {
    title: "Growing teams",
    body: "Outgrowing spreadsheets and need a system that still feels light.",
  },
  {
    title: "Ops-heavy orgs",
    body: "Inventory, finance, and field work that need ERP and reliable hardware.",
  },
  {
    title: "Service businesses",
    body: "Client pipelines, scheduling, and internal tools that should share one SSO.",
  },
  {
    title: "IT caretakers",
    body: "People who keep desks online and want a repair partner they can call.",
  },
] as const;

export const PROCESS_STEPS = [
  {
    n: "01",
    title: "Discover",
    body: "Map how work really happens — constraints, systems, and the outcome you need.",
  },
  {
    n: "02",
    title: "Design",
    body: "Architecture and scope that fit your stack, timeline, and people on the floor.",
  },
  {
    n: "03",
    title: "Deliver",
    body: "Build, integrate, or repair with clear milestones and practical demos along the way.",
  },
  {
    n: "04",
    title: "Support",
    body: "Stay close after go-live — refinements, parts, and hands-on help when something breaks.",
  },
] as const;

export const JD_APPS = [
  {
    id: "mail",
    name: "JD Mail",
    blurb: "Team inbox with the same warm HUD language as the suite.",
    env: "NEXT_PUBLIC_MAIL_URL",
    icon: "/apps/mail.webp",
  },
  {
    id: "drive",
    name: "JD Drive",
    blurb: "Org file storage beside mail and CMS — same SSO and chrome.",
    env: "NEXT_PUBLIC_DRIVE_URL",
    icon: "/apps/drive.png",
  },
  {
    id: "calendar",
    name: "Calendar",
    blurb: "Schedules and availability shared across the org.",
    env: "NEXT_PUBLIC_CALENDAR_URL",
    icon: "/apps/calendar.webp",
  },
  {
    id: "task",
    name: "Task",
    blurb: "Work queues that stay next to mail and calendar.",
    env: "NEXT_PUBLIC_TASK_URL",
    icon: "/apps/task.webp",
  },
  {
    id: "account",
    name: "Account",
    blurb: "Org hub — tenants, mailboxes, and JD Apps profiles.",
    env: "NEXT_PUBLIC_ACCOUNT_URL",
    icon: "/apps/account.webp",
  },
  {
    id: "cms",
    name: "CMS",
    blurb: "Posts, pages, and media that power the public site.",
    env: "NEXT_PUBLIC_CMS_URL",
    icon: "/apps/cms.webp",
  },
  {
    id: "analytics",
    name: "Analytics",
    blurb: "First-party traffic insights for jdintegrations.net.",
    env: "NEXT_PUBLIC_ANALYTICS_URL",
    icon: "/apps/analytics.webp",
  },
  {
    id: "crm",
    name: "CRM",
    blurb: "Leads, deals, and activity — fed by the public site.",
    env: "NEXT_PUBLIC_CRM_URL",
    icon: "/apps/crm.webp",
  },
  {
    id: "erp",
    name: "ERP",
    blurb: "Eight modules — activate what you need as you grow.",
    env: "NEXT_PUBLIC_ERP_URL",
    icon: "/apps/erp.webp",
  },
] as const;

export type JdAppMeshId = (typeof JD_APPS)[number]["id"];

/** How JD Apps connect — hub is Account (SSO / org profiles). */
export const JD_APP_MESH: {
  from: JdAppMeshId;
  to: JdAppMeshId;
  label: string;
}[] = [
  { from: "account", to: "mail", label: "SSO · mailboxes" },
  { from: "account", to: "drive", label: "SSO · org storage" },
  { from: "account", to: "calendar", label: "SSO · people" },
  { from: "account", to: "task", label: "SSO · assignees" },
  { from: "account", to: "crm", label: "SSO · org access" },
  { from: "account", to: "cms", label: "SSO · authors" },
  { from: "account", to: "analytics", label: "SSO · workspace" },
  { from: "account", to: "erp", label: "SSO · modules" },
  { from: "mail", to: "calendar", label: "Invites · availability" },
  { from: "mail", to: "drive", label: "Attachments · files" },
  { from: "mail", to: "task", label: "Turn threads into work" },
  { from: "calendar", to: "task", label: "Deadlines · schedules" },
  { from: "cms", to: "analytics", label: "Posts → traffic" },
  { from: "cms", to: "drive", label: "Media · uploads" },
  { from: "cms", to: "crm", label: "Site → leads" },
  { from: "crm", to: "mail", label: "Follow-ups" },
  { from: "crm", to: "task", label: "Deal work queues" },
  { from: "erp", to: "crm", label: "Orders · customers" },
  { from: "erp", to: "analytics", label: "Ops signals" },
];

const PUBLIC_APP_URLS: Record<(typeof JD_APPS)[number]["env"], string> = {
  NEXT_PUBLIC_MAIL_URL: "https://jdmail.jdintegrations.net",
  NEXT_PUBLIC_DRIVE_URL: "https://drive.jdintegrations.net",
  NEXT_PUBLIC_CALENDAR_URL: "https://calendar.jdintegrations.net",
  NEXT_PUBLIC_TASK_URL: "https://task.jdintegrations.net",
  NEXT_PUBLIC_CRM_URL: "https://crm.jdintegrations.net",
  NEXT_PUBLIC_CMS_URL: "https://cms.jdintegrations.net",
  NEXT_PUBLIC_ANALYTICS_URL: "https://analytics.jdintegrations.net",
  NEXT_PUBLIC_ACCOUNT_URL: "https://account.jdintegrations.net",
  NEXT_PUBLIC_ERP_URL: "https://erp.jdintegrations.net",
};

/** Public JD Apps links — ignore localhost env from local .env during production builds. */
export function publicAppUrl(envKey: (typeof JD_APPS)[number]["env"]): string {
  const raw = process.env[envKey]?.trim();
  if (raw && !/^https?:\/\/(127\.0\.0\.1|localhost)(:\d+)?/i.test(raw)) {
    return raw;
  }
  return PUBLIC_APP_URLS[envKey];
}

/** JD ERP pricing lives on the ERP product site — not checkout on jdintegrations.net. */
export function erpPricingUrl(): string {
  return `${publicAppUrl("NEXT_PUBLIC_ERP_URL")}/pricing`;
}

export const MANIFESTO =
  "Good systems feel quiet. They fit the way people already work — then make that work faster, clearer, and harder to break.";
